/**
 * User role constants
 * @description Defines all possible user roles in the system
 */
export const USER_ROLES = {
	SUPER_ADMIN: "super_admin",
	SYSTEM_ADMIN: "system_admin",
	SECRETARY_SD_ED: "secretary_sd&ed",
	ASSISTANT_DIRECTOR: "assistant_director",
	DEPUTY_DIRECTOR: "deputy_director",
	DIRECTOR: "director",
	ADDITIONAL_DIRECTOR: "additional_director",
	DIRECTOR_GENERAL: "director_general",
	/** Local prototype login only. Not issued by the auth API. */
	CHIEF_MINISTER: "chief_minister",
} as const;

/**
 * Type for user roles
 */
export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

/**
 * Permission constants
 * @description Defines known permissions for TypeScript type safety
 * Note: The system is dynamic and will work with any new permissions from API
 */
export const PERMISSIONS = {
	// Dashboard permissions
	/** PSDA Admin home dashboard (scrutiny KPI cards, pie/bar charts). Not for PVTC/TEVTA org users. */
	PSDA_ADMIN_DASHBOARD: "psda.admin.dashboard",

	// Institute permissions
	INSTITUTES_VIEW: "institutes.view",
	INSTITUTES_LIST: "institutes.list",

	// Application permissions
	APPLICATIONS_VIEW: "applications.view",
	APPLICATIONS_PENDING_LIST: "applications.pending.list",
	APPLICATIONS_SCRUTINIZED_LIST: "applications.scrutinized.list",
	APPLICATIONS_REFERRED_BACK_LIST: "applications.reffered_back.list",
	APPLICATIONS_INSPECTED_LIST: "applications.inspected.list",
	APPLICATIONS_CHECKED_LIST: "applications.checked.list",
	APPLICATIONS_VERIFIED_LIST: "applications.verified.list",
	APPLICATIONS_APPROVED_LIST: "applications.approved.list",
	APPLICATIONS_SCRUTINIZE: "applications.scrutinize",
	APPLICATIONS_REJECT_WITHOUT_SCRUTINY: "applications.reject_without_scrutiny",
	APPLICATIONS_SCRUTINIZED_NOTESHEET: "applications.scrutinized.notesheet",
	/** Download Application PDF for pending applications (API typo: downlaod) */
	DOWNLOAD_APPLICATION_PDF_PENDING: "downlaod.application.pdf.pending",

	// Committee permissions
	COMMITTEES_VIEW: "committees.view",
	COMMITTEES_PENDING_LIST: "committees.pending.list",
	COMMITTEES_APPROVED_LIST: "committees.approved.list",
	COMMITTEES_REJECTED_LIST: "committees.rejected.list",
	COMMITTEES_FINALIZED_LIST: "committees.finalized.list",
	COMMITTEES_CREATE: "committees.create",
	COMMITTEES_UPDATE: "committees.update",
	COMMITTEES_DELETE: "committees.delete",
	COMMITTEES_APPROVE: "committees.approve",
	COMMITTEES_REJECT: "committees.reject",
	COMMITTEES_FINALIZE: "committees.finalize",
	// My Committee (Deputy Director) permissions
	MY_COMMITTEE_PENDING_APPROVAL_LIST: "my.committee.pending.approval.list",
	MY_COMMITTEE_PENDING_APPROVAL_VIEW: "my.committee.pending.approval.view",
	MY_COMMITTEE_CONSTITUTED_LIST: "my.committee.constituted.list",
	MY_COMMITTEE_CONSTITUTED_VIEW: "my.committee.constituted.view",
	MY_COMMITTEE_APPROVED_LIST: "my.committee.approved.list",
	MY_COMMITTEE_APPROVED_VIEW: "my.committee.approved.view",
	MY_COMMITTEE_REJECTED_LIST: "my.committee.rejected.list",
	MY_COMMITTEE_REJECTED_VIEW: "my.committee.rejected.view",
	// Requests (Director) permissions - API uses "requets" typo
	REQUESTS_LISTS: "requets.lists",
	REQUESTS_VIEW: "requets.view",
	// History permissions
	HISTORY_CONSTITUTED_LIST: "history.constituted.list",
	HISTORY_CONSTITUTED_VIEW: "history.constituted.view",
	HISTORY_APPROVED_LIST: "history.approved.list",
	HISTORY_APPROVED_VIEW: "history.approved.view",
	HISTORY_REJECT_LIST: "history.reject.list",
	HISTORY_REJECT_VIEW: "history.reject.view",
	COMMITTEE_MEMBER_LIST_READ: "committee_member.list.read",
	COMMITTEE_MEMBER_READ: "committee_member.read",
	COMMITTEE_MEMBER_CREATE: "committee_member.create",
	COMMITTEE_MEMBER_UPDATE: "committee_member.update",
	COMMITTEE_MEMBER_DELETE: "committee_member.delete",
	COMMITTEE_MEMBER_LIST_EXPORT: "committee_member.list.export",
	COMMITTEE_MEMBER_BULK_UPLOAD: "committee_member.bulk.upload",

	// Certificate permissions
	CERTIFICATES_VIEW: "certificates.view",
	CERTIFICATES_LIST: "certificates.list",
	CERTIFICATES_CREATE: "certificates.create",
	CERTIFICATES_UPDATE: "certificates.update",
	CERTIFICATES_DELETE: "certificates.delete",
	ISSUE_CERTIFICATE: "issue.certificate",

	// Member permissions
	MEMBERS_VIEW: "members.view",
	MEMBERS_CREATE: "members.create",
	MEMBERS_UPDATE: "members.update",
	MEMBERS_DELETE: "members.delete",
	MEMBERS_LIST: "members.list",

	// Course permissions
	COURSES_VIEW: "courses.view",
	COURSES_CREATE: "courses.create",
	COURSES_UPDATE: "courses.update",
	COURSES_DELETE: "courses.delete",
	COURSES_LIST: "courses.list",
	COURSES_ACTIVATE_DEACTIVATE: "courses.activate_deactivate",
	COURSES_APPROVE: "courses.approve",
	COURSES_REJECT: "courses.reject",

	// Labs permissions
	LABS_VIEW: "labs.view",
	LABS_LIST: "labs.list",
	LABS_CREATE: "labs.create",
	LABS_UPDATE: "labs.update",
	LABS_DELETE: "labs.delete",

	// Equipment permissions
	EQUIPMENTS_VIEW: "equipments.view",
	EQUIPMENTS_CREATE: "equipments.create",
	EQUIPMENTS_UPDATE: "equipments.update",
	EQUIPMENTS_DELETE: "equipments.delete",
	EQUIPMENTS_LIST: "equipments.list",

	// Annual Calendar permissions
	ANNUAL_CALENDAR_VIEW: "annual_calendar.view",
	ANNUAL_CALENDAR_LIST: "annual_calendar.list",
	ANNUAL_CALENDAR_CREATE: "annual_calendar.create",
	ANNUAL_CALENDAR_UPDATE: "annual_calendar.update",
	ANNUAL_CALENDAR_DELETE: "annual_calendar.delete",
	// Event permissions (for calendar events)
	VIEW_EVENT: "view.event",
	CREATE_EVENT: "create.event",
	EDIT_EVENT: "edit.event",
	DELETE_EVENT: "delete.event",
	ACADEMIC_CALENDAR_EVENT_LIST: "academic_calendar_event.list",
	ACADEMIC_CALENDAR_EVENT_READ: "academic_calendar_event.read",
	ACADEMIC_CALENDAR_EVENT_CREATE: "academic_calendar_event.create",
	ACADEMIC_CALENDAR_EVENT_UPDATE: "academic_calendar_event.update",
	ACADEMIC_CALENDAR_EVENT_DELETE: "academic_calendar_event.delete",
	ACADEMIC_CALENDAR_EVENT_ENDORSE: "academic_calendar_event.endorse",
	ACADEMIC_CALENDAR_EVENT_REJECT: "academic_calendar_event.reject",
	CLONE_CALENDAR_EVENT: "academic_calendar_event.clone",

	// Manage Sessions permissions
	MANAGE_SESSIONS_VIEW: "manage_sessions.view",
	MANAGE_SESSIONS_CREATE: "manage_sessions.create",
	MANAGE_SESSIONS_UPDATE: "manage_sessions.update",
	MANAGE_SESSIONS_DELETE: "manage_sessions.delete",
	MANAGE_SESSIONS_LIST: "manage_sessions.list",

	// Course Assignment permissions
	COURSE_ASSIGNMENT_READ: "course_assignment_read",
	COURSE_ASSIGNMENT_LIST: "course_assignment_list",
	COURSE_ASSIGNMENT_CREATE: "course_assignment_create",
	COURSE_ASSIGNMENT_UPDATE: "course_assignment_update",
	COURSE_ASSIGNMENT_DELETE: "course_assignment_delete",
	COURSE_ASSIGNMENT_SPECIAL_INITIATIVE_READ: "course_assignment_special_initiative_read",
	COURSE_ASSIGNMENT_SPECIAL_INITIATIVE_APPROVE: "course_assignment_special_initiative_approve",
	COURSE_ASSIGNMENT_SPECIAL_INITIATIVE_REJECT: "course_assignment_special_initiative_reject",

	// Reports permissions
	REPORTS_VIEW: "reports.view",

	// Manage Users permissions
	MANAGE_USERS_LIST: "manage_users.list",
	MANAGE_USERS_VIEW: "manage_users.view",
	MANAGE_USERS_CREATE: "manage_users.create",
	MANAGE_USERS_UPDATE: "manage_users.update",
	MANAGE_USERS_DELETE: "manage_users.delete",

	// Roles and Permissions
	ROLES_VIEW: "roles.view",
	ROLES_LIST: "roles.list",
	ROLES_CREATE: "roles.create",
	ROLES_UPDATE: "roles.update",
	ROLES_DELETE: "roles.delete",

	// Audit Logs permissions
	AUDIT_LOGS_VIEW: "audit_logs.view",

	// Traceability permissions
	TRACEABILITY_VIEW: "traceability.view",
	TRACEABILITY_LIST: "traceability.list",

	// Application Rejection permissions
	APPLICATION_REJECTION_VIEW: "application_rejection.view",
	APPLICATION_REJECTION_PENDING_LIST: "application_rejection.pending.list",
	APPLICATION_REJECTION_APPROVED_LIST: "application_rejection.approved.list",
	APPLICATION_REJECTION_REJECTED_LIST: "application_rejection.rejected.list",
	APPLICATION_REJECTION_CANCELLED_LIST: "application_rejection.cancelled.list",
	APPLICATION_REJECTION_CANCEL: "application_rejection.cancel",
	APPLICATION_REJECTION_VERIFY: "application_rejection.verify",
	APPLICATION_REJECTION_APPROVE: "application_rejection.approve",
	APPLICATION_REJECTION_REJECT: "application_rejection.reject",
	// View Application Rejection per tab
	VIEW_PENDING: "view.pending",
	VIEW_APPROVE: "view.approve",
	VIEW_CANCEL: "view.cancel",
	// Download Application Rejection PDF per tab (API typos preserved)
	DOWNLOAD_REJECTION_PDF_PENDING: "downlaod.application.rejection.pdf.pending",
	DOWNLOAD_REJECTION_PDF_APPROVE: "downlaod.application.rejection.pdf.appove",
	DOWNLOAD_REJECTION_PDF_REJECT: "downlaod.application.rejection.pdf.reject",
	DOWNLOAD_REJECTION_PDF_CANCEL: "downlaod.application.rejection.pdf.cancel",

	// Application Appeal permissions
	APPLICATION_APPEAL_VIEW: "application_appeal.view",
	APPLICATION_APPEAL_PENDING_LIST: "application_appeal.pending.list",

	// Configuration permissions (module-level `.all` unlocks full UI for that submodule)
	VIEW_CONFIGURATION: "view_configuration",
	UPDATE_CONFIGURATION: "update_configuration",
	UPDATE_COURSE_SCHEME: "update.course.scheme",
	UPDATE_COURSE_SCHEME_VOCATIONAL: "update.course.scheme.vocational",
	UPDATE_COURSE_SCHEME_TECHNICAL: "update.course.scheme.technical",
	/** Full Course Stream configuration (enable/disable Technical & Vocational streams). */
	COURSE_STREAM_CONFIGURATION_ALL: "course.stream.configuration.all",
	ORGANIZATION_CONFIGURATION_ALL: "organization.configuration.type.all",
	ORGANIZATION_CONFIGURATION_CREATE: "organization.configuration.create",
	ORGANIZATION_CONFIGURATION_DELETE: "organization.configuration.delete",
	ORGANIZATION_CONFIGURATION_LIST: "organization.configuration.list",
	ORGANIZATION_CONFIGURATION_VIEW: "organization.configuration.view",
	COURSE_CONTENT_CONFIGURATION_ALL: "course.content.configuration.all",
	COURSE_CONTENT_CONFIGURATION_CREATE: "course.content.configuration.create",
	COURSE_CONTENT_CONFIGURATION_DELETE: "course.content.configuration.delete",
	COURSE_CONTENT_CONFIGURATION_LIST: "course.content.configuration.list",
	COURSE_CONTENT_CONFIGURATION_VIEW: "course.content.configuration.view",
	ATTACHMENT_TYPE_CONFIGURATION_ALL: "attachment.type.configuration.all",
	ATTACHMENT_TYPE_CONFIGURATION_CREATE: "attachment.type.configuration.create",
	ATTACHMENT_TYPE_CONFIGURATION_DELETE: "attachment.type.configuration.delete",
	ATTACHMENT_TYPE_CONFIGURATION_LIST: "attachment.type.configuration.list",
	ATTACHMENT_TYPE_CONFIGURATION_VIEW: "attachment.type.configuration.view",
	ATTACHMENT_OPTION_CONFIGURATION_ALL: "attachment.option.configuration.all",
	ATTACHMENT_OPTION_CONFIGURATION_CREATE: "attachment.option.configuration.create",
	ATTACHMENT_OPTION_CONFIGURATION_DELETE: "attachment.option.configuration.delete",
	ATTACHMENT_OPTION_CONFIGURATION_LIST: "attachment.option.configuration.list",
	ATTACHMENT_OPTION_CONFIGURATION_VIEW: "attachment.option.configuration.view",
	INSTITUTE_FACILITY_CONFIGURATION_ALL: "institute.facility.configuration.all",
	INSTITUTE_FACILITY_CONFIGURATION_CREATE: "institute.facility.configuration.create",
	INSTITUTE_FACILITY_CONFIGURATION_DELETE: "institute.facility.configuration.delete",
	INSTITUTE_FACILITY_CONFIGURATION_LIST: "institute.facility.configuration.list",
	INSTITUTE_FACILITY_CONFIGURATION_VIEW: "institute.facility.configuration.view",
	QUALIFICATION_CONFIGURATION_ALL: "qualification.configuration.all",
	QUALIFICATION_CONFIGURATION_CREATE: "qualification.configuration.create",
	QUALIFICATION_CONFIGURATION_DELETE: "qualification.configuration.delete",
	QUALIFICATION_CONFIGURATION_LIST: "qualification.configuration.list",
	QUALIFICATION_CONFIGURATION_VIEW: "qualification.configuration.view",
	COMPETENCY_POINT_CONFIGURATION_ALL: "competency.point.all",
	/** Full Sector configuration CRUD — assign in RBAC to PSDA, TEVTA, and PVTC admin roles. */
	SECTOR_CONFIGURATION_ALL: "sector.configuration.all",
	/** Full Course Scheme configuration CRUD — assign in RBAC to TEVTA and PVTC admin roles only. */
	COURSE_SCHEME_CONFIGURATION_ALL: "course.scheme.configuration.all",
	/** Full Assessment Body configuration CRUD. */
	ASSESSMENT_BODY_CONFIGURATION_ALL: "assessment.body.configuration.all",

	// FAQ permissions
	FAQS_VIEW: "view.faqs",
	FAQS_EDIT: "edit.faqs",
	FAQS_LIST: "list.faqs",
	FAQS_CREATE: "create.faqs",
	FAQS_DELETE: "delete.faqs",

	// Unauthorized institutes permissions (new backend keys)
	UNAUTHORIZED_INSTITUTE_CREATE: "create.unauthorized.institute",
	UNAUTHORIZED_INSTITUTE_UPDATE: "update.unauthorized.institute",
	UNAUTHORIZED_INSTITUTE_DELETE: "delete.unauthorized.institute",
	UNAUTHORIZED_INSTITUTE_LIST: "list.unauthorized.institute",
	UNAUTHORIZED_INSTITUTE_VIEW: "view.unauthorized.institute",
	/** Apprenticeship — Industry (align strings with login/API permissions) */
	APPRENTICESHIP_INDUSTRY_LIST: "apprenticeship.industry.list",
	APPRENTICESHIP_INDUSTRY_VIEW: "apprenticeship.industry.view",
	APPRENTICESHIP_INDUSTRY_CREATE: "apprenticeship.industry.create",
	APPRENTICESHIP_INDUSTRY_UPDATE: "apprenticeship.industry.update",
	APPRENTICESHIP_INDUSTRY_DELETE: "apprenticeship.industry.delete",

	/** Apprenticeship — apprentice registrations */
	APPRENTICESHIP_APPRENTICE_LIST: "apprenticeship.apprentice.list",
	APPRENTICESHIP_APPRENTICE_VIEW: "apprenticeship.apprentice.view",
	APPRENTICESHIP_APPRENTICE_CREATE: "apprenticeship.apprentice.create",
	APPRENTICESHIP_APPRENTICE_UPDATE: "apprenticeship.apprentice.update",
	APPRENTICESHIP_APPRENTICE_DELETE: "apprenticeship.apprentice.delete",

	/** Apprenticeship — completion & traceability (same list as apprentices) */
	APPRENTICESHIP_OUTCOMES_LIST: "apprenticeship.outcomes.list",
	APPRENTICESHIP_OUTCOMES_VIEW: "apprenticeship.outcomes.view",
	APPRENTICESHIP_OUTCOMES_UPDATE: "apprenticeship.outcomes.update",

	/** Apprenticeship — institute dashboard summary (year-based KPI cards) */
	APPRENTICESHIP_DASHBOARD_SUMMARY: "apprenticeship.dashboard.summary",

	/** SDED employer (employee) endorsement — list/view/approve/reject from login API */
	EMPLOYER_LIST: "employer.list",
	EMPLOYER_VIEW: "employer.view",
	EMPLOYER_APPROVE: "employer.approve",
	EMPLOYER_REJECT: "employer.reject",

	/** Executive / leadership dashboards (login API permissions) */
	SECRETARY_SD_ED_DASHBOARD: "Secretary.sd&ed.dashboard",
	PROJECT_DIRECTOR_DASHBOARD: "project.director.dashboard",
	CHAIRMAN_TEVTA_DASHBOARD: "chairman.tevta.dashboard",
	CHAIRMAN_PVTC_DASHBOARD: "chairman.pvtc.dashboard",
	/** PBTE Assessment Body chairman dashboard (admin portal; same data as tvet-assessment-portal chairman view). */
	CHAIRMAN_PBTE_DASHBOARD: "chairman.pbte.dashboard",
	DG_PSDA_DASHBOARD: "dg.psda.dashboard",

	// General permissions (super admin)
	ALL_READ: "all.read",
} as const;

export const COURSE_STREAM_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.COURSE_STREAM_CONFIGURATION_ALL,
	PERMISSIONS.UPDATE_COURSE_SCHEME_VOCATIONAL,
	PERMISSIONS.UPDATE_COURSE_SCHEME_TECHNICAL,
	PERMISSIONS.VIEW_CONFIGURATION,
	PERMISSIONS.UPDATE_CONFIGURATION,
] as const;

export const ORGANIZATION_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.ORGANIZATION_CONFIGURATION_ALL,
] as const;

export const COURSE_CONTENT_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.COURSE_CONTENT_CONFIGURATION_ALL,
] as const;

export const QUALIFICATION_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.QUALIFICATION_CONFIGURATION_ALL,
] as const;

export const ATTACHMENT_TYPE_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.ATTACHMENT_TYPE_CONFIGURATION_ALL,
] as const;

export const ATTACHMENT_OPTION_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.ATTACHMENT_OPTION_CONFIGURATION_ALL,
] as const;

export const INSTITUTE_FACILITY_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.INSTITUTE_FACILITY_CONFIGURATION_ALL,
] as const;

export const COMPETENCY_POINT_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.COMPETENCY_POINT_CONFIGURATION_ALL,
] as const;

export const SECTOR_CONFIGURATION_PERMISSIONS = [PERMISSIONS.SECTOR_CONFIGURATION_ALL] as const;

export const COURSE_SCHEME_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.COURSE_SCHEME_CONFIGURATION_ALL,
] as const;

export const ASSESSMENT_BODY_CONFIGURATION_PERMISSIONS = [
	PERMISSIONS.ASSESSMENT_BODY_CONFIGURATION_ALL,
] as const;

/** Sidebar / route access: one permission per configuration submodule. */
export const CONFIGURATION_SECTION_PERMISSIONS = [
	...COURSE_STREAM_CONFIGURATION_PERMISSIONS,
	...ORGANIZATION_CONFIGURATION_PERMISSIONS,
	...COURSE_CONTENT_CONFIGURATION_PERMISSIONS,
	...QUALIFICATION_CONFIGURATION_PERMISSIONS,
	...ATTACHMENT_TYPE_CONFIGURATION_PERMISSIONS,
	...ATTACHMENT_OPTION_CONFIGURATION_PERMISSIONS,
	...INSTITUTE_FACILITY_CONFIGURATION_PERMISSIONS,
	...COMPETENCY_POINT_CONFIGURATION_PERMISSIONS,
	...SECTOR_CONFIGURATION_PERMISSIONS,
	...COURSE_SCHEME_CONFIGURATION_PERMISSIONS,
	...ASSESSMENT_BODY_CONFIGURATION_PERMISSIONS,
] as const;

/** Permissions that grant access to the Apprenticeship sidebar section (tables). */
export const APPRENTICESHIP_SECTION_PERMISSIONS = [
	PERMISSIONS.APPRENTICESHIP_INDUSTRY_LIST,
	PERMISSIONS.APPRENTICESHIP_INDUSTRY_VIEW,
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_LIST,
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_VIEW,
	PERMISSIONS.APPRENTICESHIP_OUTCOMES_LIST,
	PERMISSIONS.APPRENTICESHIP_OUTCOMES_VIEW,
] as const;

/**
 * All known apprenticeship permission identifiers (login/API may use `.read` instead of `.view`).
 * Used for dashboard overlay detection; unknown `apprenticeship.*` keys are matched by prefix.
 */
export const APPRENTICESHIP_PERMISSIONS = [
	PERMISSIONS.APPRENTICESHIP_DASHBOARD_SUMMARY,
	...APPRENTICESHIP_SECTION_PERMISSIONS,
	PERMISSIONS.APPRENTICESHIP_INDUSTRY_CREATE,
	PERMISSIONS.APPRENTICESHIP_INDUSTRY_UPDATE,
	PERMISSIONS.APPRENTICESHIP_INDUSTRY_DELETE,
	"apprenticeship.industry.read",
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_CREATE,
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_UPDATE,
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_DELETE,
	"apprenticeship.apprentice.read",
	"apprenticeship.student.list",
	"apprenticeship.student.read",
	PERMISSIONS.APPRENTICESHIP_OUTCOMES_UPDATE,
	"apprenticeship.apprentice.completion.update",
	"apprenticeship.apprentice.traceability.upsert",
] as const;

/**
 * Permissions that grant access to home "/" (alongside psda.admin.dashboard and apprenticeship summary).
 * One dedicated dashboard permission per role variant: Secretary SD&ED, Project Director, Chairman TEVTA, Chairman PVTC.
 * DG PSDA: assign `dg.psda.dashboard` for the executive + embedded session view; optionally also `psda.admin.dashboard` on the same role
 * (Create/Edit Role allows the pair) for deputy-director-style institute dashboard access in the combined DG home page.
 */
export const EMPLOYER_ENDORSEMENT_PERMISSIONS = [
	PERMISSIONS.EMPLOYER_LIST,
	PERMISSIONS.EMPLOYER_VIEW,
	PERMISSIONS.EMPLOYER_APPROVE,
	PERMISSIONS.EMPLOYER_REJECT,
] as const;

export const EXECUTIVE_DASHBOARD_PERMISSIONS = [
	PERMISSIONS.SECRETARY_SD_ED_DASHBOARD,
	PERMISSIONS.PROJECT_DIRECTOR_DASHBOARD,
	PERMISSIONS.CHAIRMAN_TEVTA_DASHBOARD,
	PERMISSIONS.CHAIRMAN_PVTC_DASHBOARD,
	PERMISSIONS.DG_PSDA_DASHBOARD,
] as const;

/** Permissions that resolve to a dedicated home dashboard (institute, executive, PBTE chairman). */
export const HOME_DASHBOARD_ROUTE_PERMISSIONS: readonly string[] = [
	PERMISSIONS.PSDA_ADMIN_DASHBOARD,
	PERMISSIONS.CHAIRMAN_PBTE_DASHBOARD,
	...EXECUTIVE_DASHBOARD_PERMISSIONS,
];

/**
 * Home dashboard route permissions — at most one executive/chairman variant per role in the Dashboard module.
 * {@link PERMISSIONS.APPRENTICESHIP_DASHBOARD_SUMMARY} lives under Apprenticeship → Statistics in the sidebar.
 */
export const DASHBOARD_VARIANT_EXCLUSIVE_IDENTIFIERS: readonly string[] = [
	PERMISSIONS.PSDA_ADMIN_DASHBOARD,
	PERMISSIONS.CHAIRMAN_PBTE_DASHBOARD,
	...EXECUTIVE_DASHBOARD_PERMISSIONS,
];

/**
 * When multiple exclusives end up selected (e.g. Select all), keep the first match in this order.
 */
export const DASHBOARD_EXCLUSIVE_PRIORITY: readonly string[] = [
	PERMISSIONS.DG_PSDA_DASHBOARD,
	PERMISSIONS.CHAIRMAN_TEVTA_DASHBOARD,
	PERMISSIONS.CHAIRMAN_PVTC_DASHBOARD,
	PERMISSIONS.CHAIRMAN_PBTE_DASHBOARD,
	PERMISSIONS.SECRETARY_SD_ED_DASHBOARD,
	PERMISSIONS.PROJECT_DIRECTOR_DASHBOARD,
	PERMISSIONS.PSDA_ADMIN_DASHBOARD,
];

/**
 * For DG PSDA–named roles only: may assign both DG executive dashboard + PSDA admin dashboard together.
 */
export const DG_PSDA_DASHBOARD_PAIR_IDENTIFIERS: readonly string[] = [
	PERMISSIONS.DG_PSDA_DASHBOARD,
	PERMISSIONS.PSDA_ADMIN_DASHBOARD,
];

/**
 * Type for permissions - allows any string for dynamic API permissions
 */
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS] | string;
